import { z } from "zod";
import shortUUID from "short-uuid";
import { useForm, zodResolver } from "@mantine/form";
import {
  Button,
  DateInput,
  Grid,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";

import {
  EvidenceSelector,
  TacticsSelectBox,
  TechniquesSelectBox,
} from "@/Features";
import diffFormData from "@/components/Forms/utils/diffFormData";
import {
  TimelineArtifactSelector,
  TimelineEventTypeSelector,
  TraceAccountSelector,
} from "@/components/Forms/components/Selectors";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

export type TimelineEventFormDefaultData = {
  uuid?: string;
  case_id: number;
  case_uuid?: string;
  created_by_id?: number | string | null;
  event_name?: string;
  timestamp?: string;
  account_id?: number | null;
  source_evidence_uuid?: string | null;
  target_evidence_uuid?: string | null;
  event_type_id?: number | null;
  artifact_id?: number | null;
  description?: string | null;
  mitre_tactic_uuid?: string | null;
  mitre_technique_uuid?: string | null;
  note_uuid?: string | null;
};

const TimelineEventSchema = z.object({
  uuid: z.string().min(10),
  event_name: z.string().min(1),
  timestamp: z.string({ message: "Required" }).datetime(),
  source_evidence_uuid: z.string().min(10).nullable().optional(),
  target_evidence_uuid: z.string().min(10).nullable().optional(),
  event_type_id: z.number().int().min(1).nullable().optional(),
  artifact_id: z.number().int().min(1).nullable().optional(),
  account_id: z.number().int().min(1).nullable().optional(),
  mitre_tactic_uuid: z.string().min(10).nullable().optional(),
  mitre_technique_uuid: z.string().min(10).nullable().optional(),
  description: z.string().nullable().optional(),
  note_uuid: z.string().min(1).nullable().optional(),
});

export const DefaultTimelineEventForm: React.FC<{
  defaultFormData: TimelineEventFormDefaultData;
  buttonProps?: {
    submitText?: string;
    cancelText?: string;
  };
  onSubmit?: (
    formData: TimelineEventFormDefaultData,
    diffData: Partial<TimelineEventFormDefaultData>
  ) => void;
  onCancel?: () => void;
}> = ({ defaultFormData, buttonProps = {}, onSubmit, onCancel }) => {
  const form = useForm<TimelineEventFormDefaultData>({
    initialValues: {
      uuid: shortUUID.uuid(),
      ...defaultFormData,
    },
    validate: zodResolver(TimelineEventSchema),
  });

  const handleSubmit = async () => {
    const validateResult = form.validate();

    if (validateResult.hasErrors) {
      return;
    }

    const formData = form.getValues();

    const diffData = diffFormData(defaultFormData, formData);

    // call onSubmit with modeled data and diff data
    // diff data is used when the form is in edit context
    // diff may be used when we only want to update modified values
    onSubmit?.(formData, diffData);
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <>
      <Grid col={2} style={{ marginBottom: 10 }}>
        <TextInput
          size="sm"
          variant="outlined"
          label="Event Name"
          placeholder="Event Name..."
          required
          description={
            "Provide a name for the event so that it can be easily identified."
          }
          colSpan={2}
          key={form.key("event_name")}
          {...form.getInputProps("event_name")}
        />
        <DateInput
          size="sm"
          required
          clearable
          enableCalendar
          label="UTC Timestamp"
          description="This is the timestamp of the event in UTC time.  This is the time that the event occurred."
          format={"YYYY-MM-DD HH:mm:ss.SSS"}
          utc={true}
          key={form.key("timestamp")}
          {...form.getInputProps("timestamp")}
        />
        <TraceAccountSelector
          size="sm"
          clearable
          searchable
          label="Account"
          query={{
            case_id: defaultFormData?.case_id,
            case_uuid: defaultFormData?.case_uuid,
          }}
          placeholder="Select Account..."
          description="If available, this is the user or system account associated with the event."
          key={form.key("account_id")}
          {...form.getInputProps("account_id")}
        />
        <EvidenceSelector
          size="sm"
          clearable
          valueKey="uuid"
          label="Source Evidence"
          query={{
            case_id: defaultFormData?.case_id,
            case_uuid: defaultFormData?.case_uuid,
          }}
          placeholder="Select Evidence..."
          description="This is the evidence that is associated with this event.  This is where the event was discovered."
          key={form.key("source_evidence_uuid")}
          {...form.getInputProps("source_evidence_uuid")}
        />
        <EvidenceSelector
          size="sm"
          clearable
          valueKey="uuid"
          label="Target Evidence"
          query={{
            case_id: defaultFormData?.case_id,
            case_uuid: defaultFormData?.case_uuid,
          }}
          placeholder="Select Evidence..."
          description="This is the evidence that is associated with this event.  This is where the event was discovered."
          key={form.key("target_evidence_uuid")}
          {...form.getInputProps("target_evidence_uuid")}
        />
        <TimelineEventTypeSelector
          size="sm"
          clearable
          searchable
          label="Event Type"
          placeholder="Select Event Type..."
          description="This is the type of event that occurred at this timestamp.  This could be a user action, system event, or other type of event."
          key={form.key("event_type_id")}
          {...form.getInputProps("event_type_id")}
        />
        <TimelineArtifactSelector
          size="sm"
          clearable
          searchable
          label="Artifact"
          placeholder="Select Artifact..."
          description="This is the artifact that is associated with this event.  This could be a file, a registry key, a network connection, or other type of artifact.  This is where the event was discovered."
          key={form.key("artifact_id")}
          {...form.getInputProps("artifact_id")}
        />
        <div style={{ gridColumn: "span 2" }}>
          <TacticsSelectBox
            key={form.key("mitre_tactic_uuid")}
            {...form.getInputProps("mitre_tactic_uuid")}
            // Override the onChange event to reset the technique select box
            /**
             * Reset the technique select box when the tactic select box changes
             * prevents the user from selecting a technique that is not associated with the selected tactic
             */
            onChange={(e) => {
              const inputProps = form.getInputProps("mitre_tactic_uuid");
              inputProps.onChange(e);
              form.setValues({ mitre_technique_uuid: null });
            }}
          />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <TechniquesSelectBox
            query={{ tactic: form?.values?.mitre_tactic_uuid }}
            disabled={!form?.values?.mitre_tactic_uuid}
            key={form.key("mitre_technique_uuid")}
            {...form.getInputProps("mitre_technique_uuid")}
          />
        </div>
        <TextAreaInput
          size="sm"
          variant="outlined"
          label="Description"
          placeholder="Enter event description"
          description={
            "Provide a brief description of the event so that it can be easily identified."
          }
          colSpan={2}
          minRows={8}
          maxRows={24}
          key={form.key("description")}
          {...form.getInputProps("description")}
        />
      </Grid>
      <ButtonMenu>
        <Button size="xs" variant="subtle" onClick={handleCancel}>
          {buttonProps.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
        >
          {buttonProps.submitText || "Submit"}
        </Button>
      </ButtonMenu>
    </>
  );
};
